const NBSP = ' ';

/**
 * Converte um total (string BR "1.234,56" / US "1234.56" / número) em Number.
 * Heurística: se há vírgula E ponto, o último separador é o decimal. Se só há
 * vírgula, é decimal (BR). Se só há ponto (ou só dígitos), trata como decimal JS
 * — isto cobre o caso comum de produtores programáticos enviarem "180.00".
 * Retorna NaN se não for parseável. Fonte única de verdade compartilhada com o
 * dashboard para evitar divergência de exibição entre telas.
 */
export function parseMoeda(valor: number | string | null | undefined): number {
  if (valor == null || valor === '') return NaN;
  if (typeof valor === 'number') return valor;
  let s = String(valor).replace(/[^0-9,.\-]/g, '');
  if (!s) return NaN;
  const ultVirgula = s.lastIndexOf(',');
  const ultPonto = s.lastIndexOf('.');
  if (ultVirgula > -1 && ultPonto > -1) {
    if (ultVirgula > ultPonto) {
      s = s.replace(/\./g, '').replace(',', '.'); // BR: ponto=milhar, vírgula=decimal
    } else {
      s = s.replace(/,/g, ''); // US: vírgula=milhar
    }
  } else if (ultVirgula > -1) {
    s = s.replace(',', '.'); // só vírgula → decimal BR
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export function formatBRL(valor: number | string | null | undefined): string {
  if (valor == null || valor === '') return `R$${NBSP}—`;
  const n = parseMoeda(valor);
  if (!Number.isFinite(n)) return `R$${NBSP}—`;
  return `R$${NBSP}${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatTicket(ticket: string | null | undefined): string {
  if (!ticket) return '#000';
  if (ticket.startsWith('#')) return ticket;
  return `#${String(ticket).padStart(3, '0')}`;
}

export function formatKm(metros: number | null | undefined): string {
  if (metros == null || !Number.isFinite(metros)) return '—';
  const km = metros / 1000;
  return `${km.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${NBSP}km`;
}

export function formatDuracao(segundos: number | null | undefined): string {
  if (segundos == null || !Number.isFinite(segundos)) return '—';
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min}${NBSP}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

export function tempoRelativo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '—';
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 30) return 'agora';
  if (diffSec < 90) return 'há 1 min';
  if (diffSec < 3600) return `há ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 7200) return 'há 1 h';
  if (diffSec < 86400) return `há ${Math.floor(diffSec / 3600)} h`;
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  if (diffSec < 86400 * 2) return `ontem, ${hh}:${mm}`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

/**
 * Constrói URL do Google Maps Directions a partir de um ponto de partida e
 * uma sequência de paradas (na ordem da rota otimizada).
 * Última parada vira destination; demais viram waypoints na ordem.
 */
export function googleMapsRouteUrl(
  partida: { lat: number; lng: number },
  paradas: Array<{ latitude: number; longitude: number }>,
): string | null {
  if (!paradas.length) return null;
  const fmt = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;
  const origin = fmt(partida.lat, partida.lng);
  const stops = paradas.map((p) => fmt(p.latitude, p.longitude));
  const destination = stops[stops.length - 1];
  const waypoints = stops.slice(0, -1).join('|');
  const params = new URLSearchParams({
    api: '1',
    origin,
    destination,
    travelmode: 'driving',
  });
  if (waypoints) params.set('waypoints', waypoints);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export function horaCurta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '—';
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}
